import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, buildWhatsAppUrl } from "../lib/api";
import { Send, Sparkles } from "lucide-react";

export default function LeadForm({ properties, preselect, content = {} }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", property_interest: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselect) {
      setForm((f) => ({ ...f, property_interest: preselect.name }));
    }
  }, [preselect]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        property_interest: form.property_interest || null,
        message: form.message || "",
      });
      toast.success("¡Bienvenido al cuento! Te contactaremos en breve.");
      const text = `Hola Pextlán, soy ${form.name}.${form.property_interest ? ` Me interesa el lote "${form.property_interest}".` : ""} Mi teléfono: ${form.phone}. ${form.message || ""}`.trim();
      window.open(buildWhatsAppUrl(text), "_blank");
      setForm({ name: "", phone: "", email: "", property_interest: "", message: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-28 text-[#F2EFE6] overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#15301E] via-[#0B1A0F] to-[#15301E]" />
      <div className="absolute inset-0 paper-grain opacity-50" />
      <div className="orb w-[480px] h-[480px] bg-[#F1C36B]/30 top-[-180px] left-[-140px]" />
      <div className="orb w-[400px] h-[400px] bg-[#B8E07A]/30 bottom-[-120px] right-[-120px]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium text-[#F1C36B]">
            <Sparkles className="w-4 h-4" /> {content.contact_overline || "Reserva tu lugar en el cuento"}
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {content.contact_title_1 || "Apartemos tu lote"}{" "}
            <span className="font-fairy text-[#B8E07A]">{content.contact_title_fairy || "antes de que se vaya"}</span>
          </h2>
          <p className="mt-5 max-w-md text-[#F2EFE6]/80 leading-relaxed">
            {content.contact_description || "Un asesor humano te responde en menos de 2 horas hábiles. Sin presión comercial — solo información clara, transparente y mucha emoción."}
          </p>
          <ul className="mt-8 space-y-3 text-[#F2EFE6]/85 text-sm">
            {(content.contact_bullets || "Visita guiada gratuita al bosque\nAsesoría legal y escrituración incluida\n2 lotes en preventa a $1,000/m² disponibles\n12 lotes regulares desde $1,500/m²")
              .split("\n").filter(Boolean).map((b, i) => (
                <li key={i}>· {b}</li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="lead-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative rounded-3xl bg-[#0B1A0F]/70 backdrop-blur-xl text-[#F2EFE6] p-7 sm:p-10 border border-[#B8E07A]/25 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.7)]"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B8E07A]">Nombre</label>
              <Input
                data-testid="lead-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Tu nombre"
                className="mt-1 bg-[#15301E] border-[#B8E07A]/20 text-[#F2EFE6] placeholder:text-[#F2EFE6]/40 rounded-xl"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B8E07A]">Teléfono</label>
              <Input
                data-testid="lead-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="228 000 0000"
                className="mt-1 bg-[#15301E] border-[#B8E07A]/20 text-[#F2EFE6] placeholder:text-[#F2EFE6]/40 rounded-xl"
                required
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-[#B8E07A]">Email (opcional)</label>
            <Input
              data-testid="lead-email"
              value={form.email}
              onChange={update("email")}
              placeholder="tu@email.com"
              type="email"
              className="mt-1 bg-[#15301E] border-[#B8E07A]/20 text-[#F2EFE6] placeholder:text-[#F2EFE6]/40 rounded-xl"
            />
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-[#B8E07A]">Lote de interés</label>
            <Select value={form.property_interest} onValueChange={update("property_interest")}>
              <SelectTrigger
                data-testid="lead-property"
                className="mt-1 bg-[#15301E] border-[#B8E07A]/20 text-[#F2EFE6] rounded-xl"
              >
                <SelectValue placeholder="Selecciona uno (opcional)" />
              </SelectTrigger>
              <SelectContent className="bg-[#15301E] text-[#F2EFE6] border-[#B8E07A]/20">
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.name}>
                    Lote #{p.lot_number} · {p.name}{p.is_presale ? " · PREVENTA" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-[#B8E07A]">Mensaje</label>
            <Textarea
              data-testid="lead-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Cuéntanos qué buscas..."
              rows={3}
              className="mt-1 bg-[#15301E] border-[#B8E07A]/20 text-[#F2EFE6] placeholder:text-[#F2EFE6]/40 rounded-xl"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="lead-submit"
            className="mt-6 w-full rounded-full bg-gradient-to-r from-[#F1C36B] via-[#FFF7C2] to-[#B8E07A] hover:opacity-95 text-[#0B1A0F] h-12 btn-lift font-bold"
          >
            {loading ? "Enviando..." : (
              <span className="inline-flex items-center gap-2">
                {content.contact_submit_label || "Cruzar el portal"} <Send className="w-4 h-4" />
              </span>
            )}
          </Button>
          <p className="mt-3 text-[11px] text-[#F2EFE6]/60 text-center">
            {content.contact_disclaimer || "Al enviar aceptas ser contactado por WhatsApp o teléfono."}
          </p>
        </motion.form>
      </div>
    </section>
  );
}
